import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Package, Truck, CheckCircle2, ExternalLink, Clock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import EmptyState from '../components/EmptyState';

interface Item {
  name: string;
  quantity: number;
  unit?: string;
  notes?: string;
}

interface PartsOrder {
  id: string;
  order_number: string;
  job_id: string;
  job_number?: string;
  job_title?: string;
  supplier?: string;
  items: Item[];
  status: string;
  ordered_by: string;
  ordered_at: string;
  received_at?: string;
}

const STATUSES = ['All', 'Ordered', 'Received'];

export default function PartsOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'Ordered'; // default: outstanding
  const [orders, setOrders] = useState<PartsOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState(
    STATUSES.includes(initialStatus) ? initialStatus : 'Ordered'
  );
  const [receivingId, setReceivingId] = useState<string | null>(null);

  // Keep URL ?status= in sync so the filter is shareable / deep-linkable
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (statusFilter === 'Ordered') next.delete('status'); // default → clean URL
    else next.set('status', statusFilter);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/parts-orders');
      setOrders(res.data || []);
    } catch (e) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = orders;
    if (statusFilter !== 'All') list = list.filter((o) => o.status === statusFilter);
    const raw = q.trim();
    const ql = raw.toLowerCase();
    // Normalize (strip non-alphanumerics) so "PO20260001", "po 2026-0001", "PO-J2026-0001"
    // all match against the canonical stored "PO-J2026-0001-01".
    const qNorm = ql.replace(/[^a-z0-9]/g, '');
    if (ql) {
      list = list.filter((o) => {
        const po = (o.order_number || '').toLowerCase();
        const poNorm = po.replace(/[^a-z0-9]/g, '');
        const jn = (o.job_number || '').toLowerCase();
        const jnNorm = jn.replace(/[^a-z0-9]/g, '');
        return (
          po.includes(ql) ||
          (qNorm.length >= 2 && (poNorm.includes(qNorm) || jnNorm.includes(qNorm))) ||
          o.supplier?.toLowerCase().includes(ql) ||
          jn.includes(ql) ||
          o.job_title?.toLowerCase().includes(ql) ||
          o.items.some((it) => it.name.toLowerCase().includes(ql))
        );
      });
    }
    return list;
  }, [orders, q, statusFilter]);

  const markReceived = async (id: string) => {
    setReceivingId(id);
    try {
      await api.put(`/parts-orders/${id}`, { status: 'Received' });
      toast.success('Marked as received');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setReceivingId(null);
    }
  };

  const outstandingCount = orders.filter((o) => o.status === 'Ordered').length;
  const receivedCount = orders.filter((o) => o.status === 'Received').length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Parts orders</h1>
          <p className="text-slate-500 mt-1">
            All purchase orders — track deliveries and mark items received.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <button
            onClick={() => setStatusFilter('Ordered')}
            className={`text-left px-3 py-2 rounded-md transition ${
              statusFilter === 'Ordered'
                ? 'bg-amber-100 ring-1 ring-amber-300'
                : 'hover:bg-amber-50'
            }`}
            data-testid="filter-outstanding-btn"
          >
            <div className="text-2xl font-bold text-amber-700 leading-none">{outstandingCount}</div>
            <div className="text-xs text-slate-500 font-semibold mt-1">Outstanding</div>
          </button>
          <button
            onClick={() => setStatusFilter('Received')}
            className={`text-left px-3 py-2 rounded-md transition ${
              statusFilter === 'Received'
                ? 'bg-emerald-100 ring-1 ring-emerald-300'
                : 'hover:bg-emerald-50'
            }`}
            data-testid="filter-received-btn"
          >
            <div className="text-2xl font-bold text-emerald-700 leading-none">{receivedCount}</div>
            <div className="text-xs text-slate-500 font-semibold mt-1">Received</div>
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            className="input pl-9"
            placeholder="Search by PO# (e.g. PO-J2026-0001), supplier, part or job…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="parts-search-input"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <select
            className="input pl-9 pr-8 appearance-none min-w-[180px] cursor-pointer"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && orders.length === 0 ? (
        <div className="card p-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={Package}
            title="No parts orders"
            subtitle={q ? 'Try a different search.' : 'Open a job in “Awaiting Parts” to add one.'}
          />
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {filtered.map((o) => {
            const received = o.status === 'Received';
            return (
              <div
                key={o.id}
                className={`card p-4 border ${
                  received ? 'border-emerald-200/70' : 'border-amber-200/70'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div>
                    <div className="font-mono font-bold text-slate-900">{o.order_number}</div>
                    <Link
                      to={`/jobs/${o.job_id}`}
                      className="text-xs text-brand-700 hover:text-brand-800 inline-flex items-center gap-1 mt-0.5"
                    >
                      {o.job_number && (
                        <span className="font-semibold">{o.job_number}</span>
                      )}
                      · {o.job_title || 'View job'}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                  {received ? (
                    <span className="chip bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200">
                      <CheckCircle2 className="h-3 w-3" /> Received
                    </span>
                  ) : (
                    <span className="chip bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200">
                      <Truck className="h-3 w-3" /> Ordered
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                  {o.supplier && <span><strong>{o.supplier}</strong></span>}
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {format(new Date(o.ordered_at), 'd MMM, HH:mm')}
                  </span>
                </div>

                <div className="rounded-lg bg-slate-50 border border-slate-100 divide-y divide-slate-100 mb-3">
                  {o.items.slice(0, 4).map((it, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="font-medium text-slate-800 truncate">{it.name}</span>
                      <span className="text-slate-500 font-mono shrink-0 ml-2">
                        {it.quantity} {it.unit || 'each'}
                      </span>
                    </div>
                  ))}
                  {o.items.length > 4 && (
                    <div className="px-3 py-1.5 text-xs text-slate-400">
                      +{o.items.length - 4} more
                    </div>
                  )}
                </div>

                {!received && (
                  <button
                    onClick={() => markReceived(o.id)}
                    disabled={receivingId === o.id}
                    className="btn btn-primary text-sm w-full"
                  >
                    {receivingId === o.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-3 w-3" />
                    )}
                    Mark received
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
